// src/components/CreateJiraIssueModal.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { XCircle, AlertCircle, Check, ExternalLink, Settings } from "react-feather";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { useAuth } from "../contexts/AuthContext";

const CreateJiraIssueModal = ({
  isOpen,
  onClose,
  remediation,
  remediationType,
  onSuccess
}) => {
  const [projects, setProjects] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedIssueType, setSelectedIssueType] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingIssueTypes, setLoadingIssueTypes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [jiraStatus, setJiraStatus] = useState(null);
  const apiCall = useApi();
  const navigate = useNavigate();
  const { canAdministerOrganization } = useRBAC();
  const { user } = useAuth();
  const isAdmin = canAdministerOrganization(remediation?.organizationId);

  const handleConfigureJira = () => {
    onClose();
    navigate('/settings/integrations');
  };

  useEffect(() => {
    if (isOpen) {
      checkJiraStatus();
    }
  }, [isOpen]);

  const checkJiraStatus = async () => {
    setLoading(true);
    try {
      const status = await apiCall("GET", "/integrations/jira/status");
      setJiraStatus(status);

      if (status.isConfigured && !status.requiresReauthorization) {
        await fetchProjects();
        await fetchPriorities();
      }
    } catch (err) {
      setError("Failed to check Jira connection");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await apiCall("GET", "/jira/projects");
      setProjects(data || []);
    } catch (err) {
      setError("Failed to load Jira projects");
    }
  };

  const fetchPriorities = async () => {
    try {
      const data = await apiCall("GET", "/jira/priorities");
      setPriorities(data || []);
      // Auto-select based on remediation priority
      const priorityMap = {
        'CRITICAL': 'Highest',
        'HIGH': 'High',
        'MEDIUM': 'Medium',
        'LOW': 'Low'
      };
      const remPriority = getRemediationPriority()?.toUpperCase();
      const mappedPriority = priorityMap[remPriority] || 'Medium';
      const match = data?.find(p => p.name.toLowerCase() === mappedPriority.toLowerCase());
      if (match) {
        setSelectedPriority(match.id);
      }
    } catch (err) {}
  };

  const fetchIssueTypes = async (projectKey) => {
    setLoadingIssueTypes(true);
    try {
      const data = await apiCall("GET", `/jira/projects/${projectKey}/issuetypes`);
      setIssueTypes(data || []);
      // Auto-select "Task" or first available
      const taskType = data?.find(t => t.name.toLowerCase() === 'task');
      setSelectedIssueType(taskType?.id || data?.[0]?.id || "");
    } catch (err) {
      setError("Failed to load issue types");
    } finally {
      setLoadingIssueTypes(false);
    }
  };

  const handleProjectChange = (e) => {
    const projectKey = e.target.value;
    setSelectedProject(projectKey);
    setSelectedIssueType("");
    setIssueTypes([]);
    if (projectKey) {
      fetchIssueTypes(projectKey);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!selectedProject) {
      setError("Please select a Jira project");
      return;
    }

    if (!selectedIssueType) {
      setError("Please select an issue type");
      return;
    }

    setSubmitting(true);

    try {
      const result = await apiCall("POST", `/jira/remediations/${remediationType}/${getRemediationId()}/link`, {
        projectKey: selectedProject,
        issueTypeId: selectedIssueType,
        priorityId: selectedPriority || null
      });

      if (result.success) {
        try {
          await apiCall("POST", `/allRemediations/logs/by-risk`, {
            remediationType,
            riskId: remediation.riskId,
            previousStatus: null,
            newStatus: result.jiraIssueKey || result.issueKey || "",
            comment: "Jira issue linked",
            changedByUserName: user?.userName || user?.email || "",
            changeType: "JiraLinked",
          });
        } catch (logErr) {}
        onSuccess?.(result);
        onClose();
      } else {
        setError(result.errorMessage || "Failed to create Jira issue");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create Jira issue");
    } finally {
      setSubmitting(false);
    }
  };

  // Helper functions to get remediation data based on type
  // All backend endpoints normalize to remediationID in their DTOs
  const getRemediationId = () => {
    return remediation.remediationID;
  };

  const getRemediationTitle = () => {
    return remediation.remediationTitle ||
           remediation.enterpriseRemediationTitle ||
           remediation.tprmRemediationTitle ||
           remediation.ransomwareRemediationTitle ||
           remediation.darkWebRemediationTitle ||
           remediation.aiRemediationTitle ||
           remediation.grcRemediationTitle ||
           "Untitled Remediation";
  };

  const getRemediationPriority = () => {
    return remediation.remediationPriority ||
           remediation.enterpriseRemediationPriority ||
           remediation.tprmRemediationPriority ||
           remediation.ransomwareRemediationPriority ||
           remediation.darkWebRemediationPriority ||
           remediation.aiRemediationPriority ||
           remediation.grcRemediationPriority ||
           "MEDIUM";
  };

  const getJiraIssueKey = () => {
    return remediation.jiraIssueKey;
  };

  if (!isOpen) return null;

  // If already linked to Jira, show the link
  if (getJiraIssueKey()) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-content-primary">Jira Issue Linked</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XCircle size={24} />
            </button>
          </div>

          <div className="mb-6">
            <div className="flex items-center mb-4">
              <Check className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-gray-700">This remediation is linked to Jira issue</span>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-lg font-medium text-blue-600">{getJiraIssueKey()}</p>
              {remediation.jiraIssueUrl && (
                <a
                  href={remediation.jiraIssueUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  View in Jira
                </a>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-content-primary">Create Jira Issue</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={submitting}
          >
            <XCircle size={24} />
          </button>
        </div>

        {loading ? (
          <div className="py-8 flex justify-center">
            <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : !jiraStatus?.isConfigured || jiraStatus?.requiresReauthorization ? (
          // Jira not configured
          (<div className="py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
                <div>
                  <p className="text-yellow-800 font-medium">
                    Jira is not configured
                  </p>
                  <p className="text-yellow-700 text-sm mt-1">
                    {isAdmin
                      ? "Configure the Jira integration to create issues from remediations."
                      : "An administrator needs to configure the Jira integration before you can create issues."}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              {isAdmin && (
                <button
                  onClick={handleConfigureJira}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Configure Jira
                </button>
              )}
            </div>
          </div>)
        ) : (
          // Jira configured - show form
          (<form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            {/* Remediation Summary */}
            <div className="mb-4 bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-500">Creating issue for:</p>
              <p className="font-medium text-content-primary">{getRemediationTitle()}</p>
            </div>
            {/* Project Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Jira Project *
              </label>
              <select
                value={selectedProject}
                onChange={handleProjectChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={submitting}
              >
                <option value="">-- Select a Project --</option>
                {projects.map((project) => (
                  <option key={project.key} value={project.key}>
                    {project.name} ({project.key})
                  </option>
                ))}
              </select>
            </div>
            {/* Issue Type Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Issue Type *
              </label>
              <select
                value={selectedIssueType}
                onChange={(e) => setSelectedIssueType(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                disabled={submitting || !selectedProject || loadingIssueTypes}
              >
                <option value="">
                  {loadingIssueTypes ? "Loading..." : "Select an issue type..."}
                </option>
                {issueTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Priority Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={submitting}
              >
                <option value="">Default</option>
                {priorities.map((priority) => (
                  <option key={priority.id} value={priority.id}>
                    {priority.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Auto-mapped from remediation priority: {getRemediationPriority()}
              </p>
            </div>
            {/* Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                disabled={submitting || !selectedProject || !selectedIssueType}
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating...
                  </>
                ) : (
                  "Create Issue"
                )}
              </button>
            </div>
          </form>)
        )}
      </div>
    </div>
  );
};

export default CreateJiraIssueModal;
